import { supabase } from '@/config/supabase';
import { LEAGUES, getLeagueForPercentile, type LeagueDef, type LeagueId, type SeasonData } from '@/models/seasons';

export class SeasonManager {
  private currentSeason: SeasonData | null = null;
  private playerLeague: LeagueId = 'bronze';
  private playerScore: number = 0;
  private playerRank: number = 0;
  private totalPlayers: number = 0;

  async loadCurrentSeason(): Promise<void> {
    const { data } = await supabase
      .from('seasons')
      .select('*')
      .eq('active', true)
      .single();

    if (data) {
      this.currentSeason = {
        id: data.id,
        name: data.name,
        startDate: data.start_date,
        endDate: data.end_date,
        active: data.active,
      };
    }
  }

  async loadPlayerRank(userId: string): Promise<void> {
    // Get total players
    const { count } = await supabase
      .from('players')
      .select('*', { count: 'exact', head: true });
    this.totalPlayers = count ?? 1;

    // Get player's rank by season_score
    const { data } = await supabase
      .from('players')
      .select('season_score, season_league')
      .eq('id', userId)
      .single();

    if (data) {
      this.playerScore = data.season_score ?? 0;
      this.playerLeague = (data.season_league as LeagueId) ?? 'bronze';
    }

    // Count players above us
    const { count: above } = await supabase
      .from('players')
      .select('*', { count: 'exact', head: true })
      .gt('season_score', this.playerScore);
    this.playerRank = (above ?? 0) + 1;
  }

  getCurrentSeason(): SeasonData | null { return this.currentSeason; }
  getPlayerLeague(): LeagueId { return this.playerLeague; }
  getPlayerScore(): number { return this.playerScore; }
  getPlayerRank(): number { return this.playerRank; }
  getTotalPlayers(): number { return this.totalPlayers; }

  getLeagueDef(): LeagueDef {
    return LEAGUES.find(l => l.id === this.playerLeague) ?? LEAGUES[LEAGUES.length - 1]!;
  }

  getPercentile(): number {
    if (this.totalPlayers <= 1) return 50;
    return ((this.playerRank - 1) / this.totalPlayers) * 100;
  }

  getSeasonTimeRemaining(): string {
    if (!this.currentSeason) return 'No active season';
    const end = new Date(this.currentSeason.endDate).getTime();
    const now = Date.now();
    const remaining = Math.max(0, end - now);
    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    return `${days}d ${hours}h remaining`;
  }
}
